import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  LayoutDashboard,
  Users,
  Brain,
  Zap,
  Plug,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  Search,
  Bell,
  ChevronDown,
  Plus,
  BarChart3,
  Users2,
  Code,
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  Trash2,
} from 'lucide-react';
import { useAuthStore } from '../../store/auth';
import { useNotificationStore, type Notification } from '../../store/notifications';
import { useThemeStore } from '../../store/theme';
import ThemeToggle from '../ThemeToggle';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'AI Employees', href: '/dashboard/employees', icon: Users },
  { name: 'Brain AI', href: '/dashboard/brain', icon: Brain },
  { name: 'Tasks', href: '/dashboard/tasks', icon: Zap },
  { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart3 },
  { name: 'Team', href: '/dashboard/team', icon: Users2 },
  { name: 'Integrations', href: '/dashboard/integrations', icon: Plug },
  { name: 'API', href: '/dashboard/api', icon: Code },
  { name: 'Billing', href: '/dashboard/billing', icon: CreditCard },
  { name: 'Settings', href: '/dashboard/settings', icon: Settings },
];

// Notification icon component
const NotificationIcon = ({ type }: { type: Notification['type'] }) => {
  switch (type) {
    case 'success':
      return <CheckCircle className="w-5 h-5 text-accent-green" />;
    case 'error':
      return <AlertCircle className="w-5 h-5 text-accent-red" />;
    case 'warning':
      return <AlertTriangle className="w-5 h-5 text-accent-orange" />;
    default:
      return <Info className="w-5 h-5 text-accent-cyan" />;
  }
};

// Format relative time
const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const {
    notifications,
    unreadCount,
    isOpen: isNotificationOpen,
    togglePanel,
    closePanel,
    markAsRead,
    markAllAsRead,
    removeNotification,
  } = useNotificationStore();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Close sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        closePanel();
      }
    };

    if (isNotificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationOpen, closePanel]);

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
      closePanel();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
      {/* Mobile Sidebar Backdrop */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-[280px] sm:w-64 border-r border-border z-50 transform transition-all duration-300 lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isDark ? 'bg-dark-800' : 'bg-white'}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`h-14 sm:h-16 flex items-center justify-between px-3 sm:px-4 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
            <Link to="/dashboard" className="flex items-center space-x-2">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-accent flex items-center justify-center">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>SweetPilot</span>
            </Link>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className={`lg:hidden p-1.5 ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 sm:p-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-3 py-2 sm:py-2.5 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-accent text-white shadow-glow-sm'
                      : isDark
                        ? 'text-text-secondary hover:bg-dark-700 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium text-sm sm:text-base">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Credits Display */}
          <div className={`p-3 sm:p-4 border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
            <div className={`p-3 sm:p-4 rounded-xl ${isDark ? 'glass-card' : 'bg-slate-50 border border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs sm:text-sm ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>Credits</span>
                <span className="badge-purple text-[10px] sm:text-xs">
                  {user?.subscription_tier || 'Free'}
                </span>
              </div>
              <div className="flex items-end justify-between">
                <span className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {user?.credits_remaining?.toLocaleString() || 0}
                </span>
                <Link
                  to="/dashboard/billing"
                  className="text-xs sm:text-sm text-accent-purple hover:text-accent-purple-light"
                >
                  Add more
                </Link>
              </div>
              <div className={`mt-2 sm:mt-3 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-dark-700' : 'bg-slate-200'}`}>
                <div
                  className="h-full bg-gradient-accent rounded-full"
                  style={{
                    width: `${Math.min(
                      ((user?.credits_remaining || 0) / 10000) * 100,
                      100
                    )}%`,
                  }}
                />
              </div>
            </div>
          </div>

          {/* User Section */}
          <div className={`p-3 sm:p-4 border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
            <div
              className={`flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 rounded-xl cursor-pointer transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            >
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-accent flex items-center justify-center flex-shrink-0">
                <span className="text-xs sm:text-sm font-semibold text-white">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs sm:text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {user?.name || 'User'}
                </p>
                <p className={`text-[10px] sm:text-xs truncate ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  {user?.email || ''}
                </p>
              </div>
              <ChevronDown
                className={`w-4 h-4 transition-transform flex-shrink-0 ${isDark ? 'text-text-muted' : 'text-slate-400'} ${
                  isUserMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </div>

            {/* User Menu Dropdown */}
            <AnimatePresence>
              {isUserMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`mt-2 p-2 rounded-xl border ${isDark ? 'bg-dark-700 border-border' : 'bg-white border-slate-200 shadow-lg'}`}
                >
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-accent-red hover:bg-accent-red/10 rounded-lg transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Log out</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className={`sticky top-0 z-30 h-14 sm:h-16 backdrop-blur-xl border-b ${isDark ? 'bg-dark-900/80 border-border' : 'bg-white/80 border-slate-200'}`}>
          <div className="h-full flex items-center justify-between px-3 sm:px-4 lg:px-8">
            {/* Left Section */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className={`lg:hidden p-1.5 sm:p-2 ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Search */}
              <div className="hidden md:block relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <input
                  type="text"
                  placeholder="Search employees, tasks..."
                  className={`w-48 lg:w-64 pl-10 pr-4 py-2 border rounded-xl text-sm focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-800 border-border text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                />
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center space-x-1.5 sm:space-x-3">
              {/* New Chat Button */}
              <Link
                to="/dashboard/employees"
                className="hidden sm:flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-accent rounded-xl text-xs sm:text-sm font-medium text-white hover:shadow-glow-sm transition-shadow"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">New Chat</span>
              </Link>

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Notifications */}
              <div ref={notificationRef} className="relative">
                <button
                  onClick={togglePanel}
                  className={`relative p-1.5 sm:p-2 rounded-xl transition-colors ${isDark ? 'text-text-muted hover:text-white hover:bg-dark-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 min-w-[16px] sm:min-w-[18px] h-[16px] sm:h-[18px] px-1 bg-accent-red rounded-full flex items-center justify-center text-[9px] sm:text-[10px] font-bold text-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Panel */}
                <AnimatePresence>
                  {isNotificationOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className={`fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-14 sm:top-full sm:mt-2 w-auto sm:w-80 md:w-96 border rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden z-50 ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}
                    >
                      {/* Header */}
                      <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-accent-purple hover:text-accent-purple-light transition-colors"
                          >
                            Mark all as read
                          </button>
                        )}
                      </div>

                      {/* Notification List */}
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="py-12 text-center">
                            <Bell className={`w-12 h-12 mx-auto mb-3 opacity-50 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                            <p className={isDark ? 'text-text-muted' : 'text-slate-500'}>No notifications yet</p>
                          </div>
                        ) : (
                          notifications.map((notification) => (
                            <div
                              key={notification.id}
                              className={`px-4 py-3 border-b last:border-0 transition-colors cursor-pointer ${isDark ? 'border-border hover:bg-dark-700/50' : 'border-slate-100 hover:bg-slate-50'} ${
                                !notification.read ? (isDark ? 'bg-dark-700/30' : 'bg-slate-50') : ''
                              }`}
                              onClick={() => handleNotificationClick(notification)}
                            >
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 mt-0.5">
                                  <NotificationIcon type={notification.type} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between">
                                    <p className={`text-sm font-medium ${!notification.read ? (isDark ? 'text-white' : 'text-slate-900') : (isDark ? 'text-text-secondary' : 'text-slate-600')}`}>
                                      {notification.title}
                                    </p>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeNotification(notification.id);
                                      }}
                                      className={`ml-2 p-1 hover:text-accent-red transition-colors ${isDark ? 'text-text-muted' : 'text-slate-400'}`}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <p className={`text-xs mt-0.5 line-clamp-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                                    {notification.message}
                                  </p>
                                  <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                                    {formatRelativeTime(notification.created_at)}
                                  </p>
                                </div>
                                {!notification.read && (
                                  <div className="flex-shrink-0">
                                    <span className="w-2 h-2 bg-accent-purple rounded-full block" />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Footer */}
                      {notifications.length > 0 && (
                        <div className={`px-4 py-2 border-t ${isDark ? 'border-border bg-dark-900/50' : 'border-slate-200 bg-slate-50'}`}>
                          <button
                            onClick={() => {
                              useNotificationStore.getState().clearAll();
                            }}
                            className={`text-xs hover:text-accent-red transition-colors flex items-center space-x-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}
                          >
                            <Trash2 className="w-3 h-3" />
                            <span>Clear all notifications</span>
                          </button>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile User Avatar */}
              <button className="lg:hidden p-1">
                <div className="w-8 h-8 rounded-full bg-gradient-accent flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-3 sm:p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
